import { watch } from 'vue';
import { useApolloClient } from '@vue/apollo-composable';
import { isGranted } from '@pcg/auth';
import * as s from '@pcg/auth/scopes';
import { IsMyPushSubscriptionDocument } from '@ugi/graphql';
import { usePushSubscription } from '@/shared/composables/usePushSubscription';
import { useAuthStore } from '@/stores/auth';

/**
 * A browser's push subscription outlives the session that made it: an expired
 * session or a sign-out from elsewhere ends the session without this tab ever
 * running `useLogout`. So whoever signs in next asks the server whether the
 * subscription this browser holds is theirs, and drops it when it is not —
 * otherwise the previous person's chat and task alerts keep appearing here.
 *
 * The same person signing in again keeps their push: an expired session is not
 * a reason to make them switch it on again.
 *
 * Should be initialized once in App.vue.
 */
export const usePushDeviceOwner = () => {
  const authStore = useAuthStore();
  const { client } = useApolloClient();
  const { getSubscribedEndpoint, unsubscribe, checkDeviceSubscription } = usePushSubscription();

  const dropForeignSubscription = async (): Promise<void> => {
    const user = authStore.user;

    // Guests cannot ask, and have nothing to claim. `useAuth()` is not used
    // here: App.vue sets up before the first `fetchMe` has resolved.
    if (!user || !isGranted(user, 'ugi:core:users:set-push-notifications', [s.user(user.id)])) {
      return;
    }

    try {
      const endpoint = await getSubscribedEndpoint();

      if (!endpoint) {
        return;
      }

      const { data } = await client.query({
        query: IsMyPushSubscriptionDocument,
        variables: { endpoint },
        fetchPolicy: 'network-only',
      });

      // Only an explicit "not yours" drops it. No answer — an API revision
      // that does not know the query yet, a network error — leaves it alone.
      if (data?.isMyPushSubscription !== false) {
        return;
      }

      // The answer is about the subscription and the person it was asked for.
      // Push switched on meanwhile re-points this very endpoint to them.
      if (authStore.user?.id !== user.id || (await getSubscribedEndpoint()) !== endpoint) {
        return;
      }

      // The server row is not ours to delete; with the local subscription gone
      // the push service answers 410 on the next delivery and it is pruned.
      await unsubscribe();
      await checkDeviceSubscription();
    } catch (error) {
      console.error(error);
    }
  };

  watch(
    () => authStore.user?.id,
    (userId) => {
      if (userId) {
        void dropForeignSubscription();
      }
    },
    { immediate: true },
  );
};
